import React, { useMemo } from 'react';
import { GivelifyTextDivider } from '@givelify/givelify-ui';
import { GivelifyLabel, DesignTokens } from '@givelify/ui';
import { useTranslation } from 'react-i18next';
import Space from '../../../../components/Space';
import {
    Container,
    Content,
    Body,
    LoginBody,
    LogoContent,
    Logo,
    Form,
    DonateBody,
    GiveButton,
    SecuredRow,
    Footer,
    LinkWithoutUnderline,
    BottomLabel,
    CopyRow,
    LockIcon,
} from './styles';

interface LoginWrapperProps {
    children?: React.ReactNode;
}

const TERMS_LINK = 'https://www.givelify.com/about/terms-of-use/';
const PRIVACY_POLICY_LINK = 'https://www.givelify.com/about/privacy-policy/';
const SUPPORT_LINK = 'https://support.givelify.com/';

const LoginWrapper: React.FCC<LoginWrapperProps> = ({ children }) => {
    const { t } = useTranslation();

    const currentYear = new Date().getFullYear();

    const loginWrapperText = useMemo(
        () => ({
            text: {
                donate: t('login.text.donate'),
                givelifyLlc: t('login.text.givelify_llc'),
                allRightsReserved: t('login.text.allRightsReserved'),
                tryingToDonate: t('login.text.tryingToDonate'),
                securedWith: t('login.text.securedWith'),
                copyRight: t('login.text.copyRight', {
                    year: currentYear,
                }),
            },
            link: {
                download: t('login.link.download'),
                signup: t('login.link.signup'),
                giveNow: t('login.link.giveNow'),
                militaryGradeEncryption: t(
                    'login.link.militaryGradeEncryption',
                ),
                termsUse: t('login.link.termsUse'),
                privacyPolicy: t('login.link.privacyPolicy'),
                support: t('login.link.support'),
            },
            copyright: t('footer.copyright'),
        }),
        [t, currentYear],
    );

    return (
        <Container>
            <Content>
                <Body>
                    <LoginBody>
                        <LogoContent>
                            <Logo title="logo" />
                        </LogoContent>
                        <Form>{children}</Form>
                    </LoginBody>
                    <GivelifyTextDivider />
                    <DonateBody>
                        <GivelifyLabel
                            color="#3A4C63"
                            fontWeight={800}
                            text={loginWrapperText.text.tryingToDonate}
                            variant="body1"
                        />
                        <GiveButton
                            color="secondary"
                            href="https://www.givelify.com/givers/"
                            name="Give Now"
                            text={loginWrapperText.link.giveNow}
                            {...{
                                target: '_blank',
                            }}
                        />
                    </DonateBody>
                </Body>
                <SecuredRow>
                    <GivelifyLabel
                        color={DesignTokens.color.textSecondary}
                        variant="body2"
                    >
                        <LockIcon
                            color={DesignTokens.color.textSecondary}
                            variant="lock-old"
                        />
                        {loginWrapperText.text.securedWith}
                    </GivelifyLabel>
                    <GivelifyLabel
                        bold
                        color={DesignTokens.color.textSecondary}
                        text={loginWrapperText.link.militaryGradeEncryption}
                        variant="body2"
                    />
                </SecuredRow>
                <Space gap={4} />
            </Content>
            <Footer>
                <CopyRow>
                    <GivelifyLabel
                        color={DesignTokens.color.textSecondary}
                        text={loginWrapperText.text.copyRight}
                        variant="body2"
                    />
                    <LinkWithoutUnderline
                        href={TERMS_LINK}
                        name="Terms of Use"
                        target="_blank"
                        text={
                            <BottomLabel
                                bold
                                color={DesignTokens.color.textSecondary}
                                text={loginWrapperText.link.termsUse}
                                variant="body2"
                            />
                        }
                    />
                    <LinkWithoutUnderline
                        href={PRIVACY_POLICY_LINK}
                        name="Privacy Policy"
                        target="_blank"
                        text={
                            <BottomLabel
                                bold
                                color={DesignTokens.color.textSecondary}
                                text={loginWrapperText.link.privacyPolicy}
                                variant="body2"
                            />
                        }
                    />
                    <LinkWithoutUnderline
                        href={SUPPORT_LINK}
                        name="Support"
                        target="_blank"
                        text={
                            <BottomLabel
                                bold
                                color={DesignTokens.color.textSecondary}
                                text={loginWrapperText.link.support}
                                variant="body2"
                            />
                        }
                    />
                </CopyRow>
            </Footer>
        </Container>
    );
};

export default LoginWrapper;
